import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ToastContainer, toast } from 'react-toastify';
import { Slide } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { HiComputerDesktop } from "react-icons/hi2";
import {
  BASE_URL,
  LOGIN,
  setRuntimeBaseUrl,
  resetBaseUrlToLocalDefault,
  applyPersistedBaseUrl,
  GET_SUBSCRIPTION_EXPIRY_DATE
} from '../utlilities/config';
import { setWebUserInfo, initializeWebUserInfo } from '../utlilities/webConfig';
import {
  FiUser,
  FiLock,
  FiActivity,
  FiArrowRight,
  FiSmartphone,
  FiEye,
  FiEyeOff
} from "react-icons/fi";
import { useUpdater } from '../components/UpdaterProvider';
import {
  generateMessageCentralToken,
  sendOtpMessageCentral,
  validateOtpMessageCentral,
  tenantMasterAuth,
} from '../utlilities/cloudLoginApi';
import Swal from 'sweetalert2';

const Spinner = () => (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-white bg-opacity-80 backdrop-blur-sm">
    <div className="flex flex-col items-center gap-3">
      <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-indigo-600"></div>
      <p className="text-indigo-600 font-medium">Loading...</p>
    </div>
  </div>
);

const isElectron = () =>
  typeof window !== 'undefined' && window.process && window.process.type;

const getIpc = () => {
  if (!isElectron()) return null;
  try {
    return window.require('electron').ipcRenderer;
  } catch {
    return null;
  }
};

// Local (on-prem) login points at a LAN API that is only reachable from the desktop build.
// The browser build is cloud-only, so the mode toggle is locked to cloud there.
const CAN_USE_LOCAL_MODE = isElectron();

const LoginPage = () => {
  const [firm, setFirm] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [departments, setDepartments] = useState([]);
  const [selectedDepartment, setSelectedDepartment] = useState('');
  const [departmentsLoading, setDepartmentsLoading] = useState(false);
  const navigate = useNavigate();

  const [sysID, setSysID] = useState('');

  const [loginMode, setLoginMode] = useState(CAN_USE_LOCAL_MODE ? 'local' : 'cloud');
  const [cloudStep, setCloudStep] = useState('mobile');
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [verificationId, setVerificationId] = useState(null);
  const [authToken, setAuthToken] = useState(null);
  const [otpTimer, setOtpTimer] = useState(60);
  const [canResendOtp, setCanResendOtp] = useState(false);
  const [dashBoardPayload, setDashBoardPayload] = useState(null);
  const [tenantAuthPayload, setTenantAuthPayload] = useState(null);
  const [tunnelClients, setTunnelClients] = useState([]);
  const [selectedTunnelClientId, setSelectedTunnelClientId] = useState('');
  const [cloudOtpVerified, setCloudOtpVerified] = useState(false);
  const [modeHydrated, setModeHydrated] = useState(false);

  const usernameRef = useRef(null);
  const passwordRef = useRef(null);
  const ipcRenderer = getIpc();

  const { currentVersion } = useUpdater();

  const getDepartments = useCallback(async () => {
    setDepartmentsLoading(true);
    try {
      const accessToken =
        'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJ4QyI6IjEiLCJ4VSI6IjEiLCJ4RiI6IlAyNllGOVU1VUU4WkpZN2VEQ2VkM2JicldkWlRDN0VuSTYvekFYMTJrOTBNemdVWlJiZFlJQU40QmlrOEozdXJKRUpCREQ0dlFvUUhiQkYybFNzVnFBVnVMckRRNG1TY0JpZnRjQVBNIiwibmJmIjoxNzczOTA1NjE5LCJleHAiOjE5MzE2NzIwMTksImlhdCI6MTc3MzkwNTYxOX0.KNLnxkk-uZ8gq8OsM2nAB_qqrqWihuDKszTDcKYj7GI';
      const response = await axios.get(`${BASE_URL}api/0202/getDepartments`, {
        headers: {
          ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
          XApiKey: process.env.REACT_APP_API_KEY,
        },
      });

      const departmentList = Array.isArray(response?.data?.data) ? response.data.data : [];
      setDepartments(departmentList);
      if (departmentList.length > 0) {
        const firstDepartmentId = String(departmentList[0].ID || '');
        setSelectedDepartment(firstDepartmentId);
      }
    } catch (error) {
      console.error('Error fetching departments:', error);
      setDepartments([]);
      setSelectedDepartment('');
    } finally {
      setDepartmentsLoading(false);
    }
  }, []);

  const getSystem = useCallback(async () => {
    if (ipcRenderer) {
      try {
        await ipcRenderer.invoke('get-system');
        let deviceId = await ipcRenderer.invoke('get-device-id');

        const loginModeStored = await ipcRenderer.invoke('store-get', 'login_mode');
        if (loginModeStored !== 'cloud') {
          const firm_namess = localStorage.getItem('firm_name');
          setFirm(firm_namess || '');
        }

        if (deviceId.startsWith('{') && deviceId.endsWith('}')) {
          deviceId = deviceId.slice(1, -1);
        }

        setSysID(deviceId);
      } catch (error) {
        console.error('Error fetching system name:', error);
      }
    } else {
      const lm = localStorage.getItem('login_mode');
      if (CAN_USE_LOCAL_MODE && lm !== 'cloud') {
        const firm_namess = localStorage.getItem('firm_name');
        setFirm(firm_namess || '');
      }
    }
  }, [ipcRenderer]);

  const persistCloudPayloads = useCallback(async (dashboard, tenant) => {
    if (ipcRenderer) {
      await ipcRenderer.invoke('store-set', 'cloud_dashboard_payload', dashboard);
      await ipcRenderer.invoke('store-set', 'cloud_tenant_auth_payload', tenant);
    } else {
      try {
        localStorage.setItem('cloud_dashboard_payload', JSON.stringify(dashboard));
        localStorage.setItem('cloud_tenant_auth_payload', JSON.stringify(tenant));
      } catch (_) {}
    }
  }, [ipcRenderer]);

  const applyTunnelClientBaseUrl = useCallback(
    async (client) => {
      const raw = (client?.tunelURL || client?.tunnelURL || '').trim();
      const host = raw.replace(/^https?:\/\//i, '').replace(/\/+$/, '');
      if (!host) return;
      const origin = `https://${host}`;
      setRuntimeBaseUrl(`${origin}/`);
      setFirm(client.clientName || '');
      if (ipcRenderer) {
        await ipcRenderer.invoke('set-api-base-url', origin);
        await ipcRenderer.invoke('store-set', 'cloud_selected_client_id', client.clientID);
        await ipcRenderer.invoke('store-set', 'cloud_selected_client', client);
      } else {
        try {
          localStorage.setItem('cloud_selected_client_id', String(client.clientID));
          localStorage.setItem('cloud_selected_client', JSON.stringify(client));
        } catch (_) {}
      }
      await getDepartments();
    },
    [ipcRenderer, getDepartments]
  );

  const persistCloudOtpVerified = useCallback(
    async (verified) => {
      setCloudOtpVerified(verified);
      if (ipcRenderer) {
        await ipcRenderer.invoke('store-set', 'cloud_otp_verified', verified);
      } else {
        try {
          if (verified) localStorage.setItem('cloud_otp_verified', '1');
          else localStorage.removeItem('cloud_otp_verified');
        } catch (_) {}
      }
    },
    [ipcRenderer]
  );

  /** Restore cloud UI from Electron store / localStorage (OTP-verified session or legacy saved tenants). */
  const applyCloudSessionFromPersistence = useCallback(async () => {
    let dash = null;
    let auth = null;
    let mob = '';
    let selId = '';
    let otpFlag;

    if (ipcRenderer) {
      otpFlag = await ipcRenderer.invoke('store-get', 'cloud_otp_verified');
      dash = await ipcRenderer.invoke('store-get', 'cloud_dashboard_payload');
      auth = await ipcRenderer.invoke('store-get', 'cloud_tenant_auth_payload');
      mob = (await ipcRenderer.invoke('store-get', 'cloud_mobile')) || '';
      selId = (await ipcRenderer.invoke('store-get', 'cloud_selected_client_id')) || '';
    } else {
      try {
        const ovRaw = localStorage.getItem('cloud_otp_verified');
        if (ovRaw === '1') otpFlag = true;
        else if (ovRaw === '0') otpFlag = false;
        else otpFlag = undefined;
        const dashRaw = localStorage.getItem('cloud_dashboard_payload');
        const authRaw = localStorage.getItem('cloud_tenant_auth_payload');
        dash = dashRaw ? JSON.parse(dashRaw) : null;
        auth = authRaw ? JSON.parse(authRaw) : null;
        mob = localStorage.getItem('cloud_mobile') || '';
        selId = localStorage.getItem('cloud_selected_client_id') || '';
      } catch (_) {
        auth = null;
      }
    }

    if (mob) setPhone(mob);
    if (dash) setDashBoardPayload(dash);

    const list =
      auth?.data && Array.isArray(auth.data)
        ? auth.data.filter((r) => (r.tunelURL || r.tunnelURL || '').trim())
        : [];

    const explicitVerified =
      otpFlag === true ||
      otpFlag === 'true' ||
      otpFlag === 1 ||
      otpFlag === '1';
    const explicitRejected = otpFlag === false || otpFlag === 'false';
    const legacySession = list.length > 0 && !explicitRejected;
    const sessionReady = explicitVerified || legacySession;

    if (!sessionReady || !list.length) {
      setTenantAuthPayload(null);
      setTunnelClients([]);
      setSelectedTunnelClientId('');
      setCloudOtpVerified(false);
      setCloudStep('mobile');
      return false;
    }

    setCloudOtpVerified(true);
    setTenantAuthPayload(auth);
    setTunnelClients(list);
    setCloudStep('credentials');
    const useId = selId ? String(selId) : String(list[0].clientID);
    setSelectedTunnelClientId(useId);
    const client =
      list.find((c) => String(c.clientID) === useId) || list[0];
    await applyTunnelClientBaseUrl(client);
    if (!explicitVerified && legacySession) {
      await persistCloudOtpVerified(true);
    }
    return true;
  }, [ipcRenderer, applyTunnelClientBaseUrl, persistCloudOtpVerified]);

  useEffect(() => {
    initializeWebUserInfo();
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      await applyPersistedBaseUrl();
      let mode = CAN_USE_LOCAL_MODE ? 'local' : 'cloud';
      if (ipcRenderer) {
        mode = (await ipcRenderer.invoke('store-get', 'login_mode')) || 'local';
      } else {
        mode = localStorage.getItem('login_mode') || 'cloud';
      }
      // Ignore any persisted 'local' outside the desktop build - the browser build cannot reach a LAN API.
      if (!CAN_USE_LOCAL_MODE && mode !== 'cloud') {
        mode = 'cloud';
        try {
          localStorage.setItem('login_mode', 'cloud');
        } catch (_) {}
      }
      if (cancelled) return;
      if (mode === 'cloud' || mode === 'local') {
        setLoginMode(mode);
      }
      if (mode === 'cloud') {
        await applyCloudSessionFromPersistence();
      }
      if (!cancelled) setModeHydrated(true);
    })();
    return () => {
      cancelled = true;
    };
  }, [ipcRenderer, applyCloudSessionFromPersistence]);

  useEffect(() => {
    getSystem();
  }, [getSystem]);

  useEffect(() => {
    if (!modeHydrated) return;
    if (loginMode === 'local') {
      getDepartments();
    }
  }, [modeHydrated, loginMode, getDepartments]);

  useEffect(() => {
    const showUserPass =
      loginMode === 'local' ||
      (loginMode === 'cloud' && cloudStep === 'credentials');
    if (showUserPass && usernameRef.current) {
      usernameRef.current.focus();
    }
  }, [loginMode, cloudStep]);

  useEffect(() => {
    const token = localStorage.getItem('accessToken');
    const designation = localStorage.getItem('user_designation');

    if (token && designation === 'Admin') {
      navigate('/dashboard');
    }
  }, [navigate]);

  useEffect(() => {
    if (cloudStep === 'otp') {
      setOtpTimer(60);
      setCanResendOtp(false);
    }
  }, [cloudStep]);

  useEffect(() => {
    if (cloudStep !== 'otp' || canResendOtp) return undefined;
    if (otpTimer <= 0) {
      setCanResendOtp(true);
      return undefined;
    }
    const id = setTimeout(() => setOtpTimer((t) => t - 1), 1000);
    return () => clearTimeout(id);
  }, [cloudStep, otpTimer, canResendOtp]);

  const handleLoginModeChange = async (mode) => {
    if (mode === loginMode) return;
    if (mode === 'local' && !CAN_USE_LOCAL_MODE) return;

    setOtp('');
    setVerificationId(null);
    setAuthToken(null);
    setOtpTimer(60);
    setCanResendOtp(false);

    if (mode === 'local') {
      setLoginMode('local');
      setCloudStep('mobile');
      resetBaseUrlToLocalDefault();
      if (ipcRenderer) {
        await ipcRenderer.invoke('store-set', 'login_mode', 'local');
        await ipcRenderer.invoke('use-local-api-base-url');
      } else {
        try {
          localStorage.setItem('login_mode', 'local');
        } catch (_) {}
      }
      await applyPersistedBaseUrl();
      await getSystem();
      await getDepartments();
      return;
    }

    setLoginMode('cloud');
    if (ipcRenderer) {
      await ipcRenderer.invoke('store-set', 'login_mode', 'cloud');
      await ipcRenderer.invoke('use-cloud-api-from-store');
    } else {
      try {
        localStorage.setItem('login_mode', 'cloud');
      } catch (_) {}
    }
    await applyPersistedBaseUrl();
    await applyCloudSessionFromPersistence();
  };

  const handleChangeCloudNumber = async () => {
    setLoading(true);
    try {
      setCloudOtpVerified(false);
      setOtp('');
      setVerificationId(null);
      setAuthToken(null);
      setDashBoardPayload(null);
      setTenantAuthPayload(null);
      setTunnelClients([]);
      setSelectedTunnelClientId('');
      setPhone('');
      setCloudStep('mobile');

      if (ipcRenderer) {
        await ipcRenderer.invoke('store-set', 'cloud_otp_verified', false);
        await ipcRenderer.invoke('store-delete', 'cloud_tenant_auth_payload');
        await ipcRenderer.invoke('store-delete', 'cloud_dashboard_payload');
        await ipcRenderer.invoke('store-delete', 'cloud_mobile');
        await ipcRenderer.invoke('store-delete', 'cloud_selected_client_id');
        await ipcRenderer.invoke('store-delete', 'cloud_selected_client');
        await ipcRenderer.invoke('set-api-base-url', '');
      } else {
        try {
          localStorage.removeItem('cloud_otp_verified');
          localStorage.removeItem('cloud_tenant_auth_payload');
          localStorage.removeItem('cloud_dashboard_payload');
          localStorage.removeItem('cloud_mobile');
          localStorage.removeItem('cloud_selected_client_id');
          localStorage.removeItem('cloud_selected_client');
        } catch (_) {}
      }
      resetBaseUrlToLocalDefault();
      toast.info('Enter a new mobile number. OTP verification will be required.');
    } finally {
      setLoading(false);
    }
  };

  const handleRefetchCloudFirms = async () => {
    let mob = phone;
    if (ipcRenderer && (!mob || mob.length !== 10)) {
      mob = (await ipcRenderer.invoke('store-get', 'cloud_mobile')) || '';
    } else if (!ipcRenderer && (!mob || mob.length !== 10)) {
      mob = localStorage.getItem('cloud_mobile') || '';
    }
    if (!mob || !/^[6-9]\d{9}$/.test(mob)) {
      toast.error('No valid saved mobile. Use “Change mobile number” first.');
      return;
    }
    if (!cloudOtpVerified && !tenantAuthPayload?.data?.length) {
      toast.error('Complete mobile verification first.');
      return;
    }

    setLoading(true);
    try {
      const tenantRes = await tenantMasterAuth(mob);
      if (!tenantRes.success || !Array.isArray(tenantRes.data)) {
        toast.error(tenantRes.message || 'Tenant lookup failed');
        return;
      }
      await persistCloudPayloads(dashBoardPayload, tenantRes);
      const list = tenantRes.data.filter(
        (r) => (r.tunelURL || r.tunnelURL || '').trim()
      );
      if (!list.length) {
        toast.error('No clients with a tunnel URL in the refreshed list');
        return;
      }
      setTenantAuthPayload(tenantRes);
      setTunnelClients(list);
      const still = list.find(
        (c) => String(c.clientID) === String(selectedTunnelClientId)
      );
      const pick = still || list[0];
      setSelectedTunnelClientId(String(pick.clientID));
      await applyTunnelClientBaseUrl(pick);
      await persistCloudOtpVerified(true);
      toast.success('Firms refreshed');
    } catch (e) {
      toast.error(e.response?.data?.message || e.message || 'Refresh failed');
    } finally {
      setLoading(false);
    }
  };

  const finishTenantStep = async (tenantRes, mobile) => {
    if (!tenantRes.success || !Array.isArray(tenantRes.data)) {
      toast.error(tenantRes.message || 'Tenant lookup failed');
      return false;
    }
    await persistCloudPayloads(dashBoardPayload, tenantRes);
    if (ipcRenderer) {
      await ipcRenderer.invoke('store-set', 'cloud_mobile', mobile);
    } else {
      try {
        localStorage.setItem('cloud_mobile', mobile);
      } catch (_) {}
    }
    const list = tenantRes.data.filter(
      (r) => (r.tunelURL || r.tunnelURL || '').trim()
    );
    if (!list.length) {
      toast.error('No clients with a tunnel URL are available for this account');
      return false;
    }
    setTenantAuthPayload(tenantRes);
    setTunnelClients(list);
    const firstId = String(list[0].clientID);
    setSelectedTunnelClientId(firstId);
    setCloudStep('credentials');
    await applyTunnelClientBaseUrl(list[0]);
    await persistCloudOtpVerified(true);
    return true;
  };

  const handleCloudMobileContinue = async () => {
    if (!/^[6-9]\d{9}$/.test(phone)) {
      toast.error('Please enter a valid 10-digit mobile number');
      return;
    }

    setLoading(true);
    try {
      const tenantRes = await tenantMasterAuth(phone);
      if (!tenantRes.success || !Array.isArray(tenantRes.data)) {
        toast.error(tenantRes.message || 'Tenant lookup failed');
        return;
      }
      const mappedClients = tenantRes.data.filter(
        (r) => (r.tunelURL || r.tunnelURL || '').trim()
      );
      if (!mappedClients.length) {
        toast.error('No mapped tenant found for this mobile number');
        return;
      }
      setTenantAuthPayload(tenantRes);
      await persistCloudPayloads(dashBoardPayload, tenantRes);
      if (ipcRenderer) {
        await ipcRenderer.invoke('store-set', 'cloud_mobile', phone);
      } else {
        try {
          localStorage.setItem('cloud_mobile', phone);
        } catch (_) {}
      }

      const token = await generateMessageCentralToken();
      setAuthToken(token);
      const otpResponse = await sendOtpMessageCentral(phone, token);
      if (!otpResponse?.data?.verificationId) {
        toast.error('Failed to send OTP');
        return;
      }
      setVerificationId(otpResponse.data.verificationId);
      setCloudStep('otp');
      setOtp('');
      setOtpTimer(60);
      setCanResendOtp(false);
      toast.success('OTP sent successfully');
    } catch (e) {
      toast.error(e.response?.data?.message || e.message || 'Request failed');
    } finally {
      setLoading(false);
    }
  };

  const handleCloudOtpVerify = async () => {
    if (!otp || otp.length !== 4) {
      toast.error('Enter the 4-digit OTP');
      return;
    }
    if (!verificationId || !authToken) {
      toast.error('Session expired. Request OTP again.');
      return;
    }
    setLoading(true);
    try {
      const response = await validateOtpMessageCentral(
        verificationId,
        otp,
        authToken
      );
      if (response.responseCode !== 200) {
        toast.error('Invalid OTP');
        return;
      }
      const tenantRes =
        tenantAuthPayload?.success && Array.isArray(tenantAuthPayload?.data)
          ? tenantAuthPayload
          : await tenantMasterAuth(phone);
      const ok = await finishTenantStep(tenantRes, phone);
      if (ok) toast.success('Verified');
    } catch (e) {
      toast.error('Invalid OTP');
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    if (!canResendOtp) return;
    setCanResendOtp(false);
    await handleCloudMobileContinue();
  };

  const handleTunnelClientChange = async (e) => {
    const id = e.target.value;
    setSelectedTunnelClientId(id);
    const c = tunnelClients.find((x) => String(x.clientID) === id);
    if (c) {
      await applyTunnelClientBaseUrl(c);
    }
  };

  const clearLoginSession = async () => {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user_designation');
    localStorage.removeItem('department_id');
    localStorage.removeItem('series_name');
    localStorage.removeItem('department_name');
    localStorage.removeItem('finac_year');

    if (ipcRenderer) {
      const loginModeStored = await ipcRenderer.invoke('store-get', 'login_mode');

      await ipcRenderer.invoke('store-set', 'clientInfo', {});
      await ipcRenderer.invoke('store-set', 'sysInfo', {});
      await ipcRenderer.invoke('store-set', 'userInfo', {});
      await ipcRenderer.invoke('store-set', 'generalsettings', {});
      await ipcRenderer.invoke('store-set', 'user_role', {});
      await ipcRenderer.invoke('store-set', 'series_name', {});
      await ipcRenderer.invoke('store-set', 'generalopsettings', {});
      await ipcRenderer.invoke('store-set', 'finac_year', {});

      if (loginModeStored === 'cloud') {
        const origin = await ipcRenderer.invoke('store-get', 'api_base_url');
        if (origin && typeof origin === 'string') {
          await ipcRenderer.invoke('set-api-base-url', origin);
        }
      }
    }

    await applyPersistedBaseUrl();
  };

  const checkSubscriptionExpiry = async (accessToken) => {
    try {
      const response = await axios.get(`${BASE_URL}${GET_SUBSCRIPTION_EXPIRY_DATE}`, {
        headers: {
          XApiKey: process.env.REACT_APP_API_KEY,
          Authorization: `Bearer ${accessToken}`,
        },
      });

      if (response.status === 200 && response.data?.isSucess) {
        const expiryDateStr = response.data?.data?.subscriptionExpiryDate;

        if (expiryDateStr) {
          const expiryDate = new Date(expiryDateStr);
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          expiryDate.setHours(0, 0, 0, 0);

          if (today > expiryDate) {
            await Swal.fire({
              icon: 'warning',
              title: 'Subscription Expired',
              text: 'Your license has expired. Please contact admin.',
              confirmButtonText: 'OK',
              allowOutsideClick: false,
              allowEscapeKey: false,
            });
            await clearLoginSession();
            return { allowLogin: false };
          }

          const MS_PER_DAY = 24 * 60 * 60 * 1000;
          const daysLeft = Math.floor((expiryDate.getTime() - today.getTime()) / MS_PER_DAY);

          if (daysLeft >= 0 && daysLeft <= 7) {
            return { allowLogin: true, showWarning: true, daysLeft };
          }
        }
      }
    } catch (error) {
      console.error('Error checking subscription expiry:', error);
    }

    return { allowLogin: true };
  };

  const handleLogin = async () => {
    if (!selectedDepartment) {
      toast.error('Please select department', {
        autoClose: 1000,
      });
      return;
    }
    if (username === '' || password === '') {
      toast.error('Please fill username or password', {
        autoClose: 1000,
      });
      return;
    }
    if (loginMode === 'local' && !firm.trim()) {
      toast.error('Please enter firm name', { autoClose: 1000 });
      return;
    }
    if (loginMode === 'cloud' && !selectedTunnelClientId) {
      toast.error('Please select a client', { autoClose: 1000 });
      return;
    }

    setLoading(true);
    try {
      const response = await axios.get(`${BASE_URL}${LOGIN}`, {
        headers: {
          TenantName: firm,
          UserCode: username,
          Password: password,
          XApiKey: process.env.REACT_APP_API_KEY,
          sysID: sysID,
        },
      });

      if (response.status === 200) {
        const accessToken = response.data.data._accessToken;
        const refreshToken = response.data.data._refreshToken || {};
        const clientInfo = response.data.data.clientInfo[0] || {};
        const sysInfo = response.data.data.sysInfo[0] || {};
        const userInfo = response.data.data.userInfo[0] || {};
        const generalsettings = response.data.data.generalSettings[0] || {};

        const receptionSettingsString =
          response.data.data.generalSettings[0].ReceptionSettings || '[]';
        const receptionSettingsArray = JSON.parse(receptionSettingsString);

        const generalOpSettings = receptionSettingsArray[0] || {};

        const userRole = response.data.data._userRoles || {};

        const generalSettings = response.data.data.generalSettings[0] || {};

        const financial_year = clientInfo?.FicialYear;
        const [startYear = '', endYear = ''] = (financial_year || '').split('-');
        const yearOnly = startYear;
        const seriesName =
          startYear && endYear ? `${startYear.slice(-2)}-${endYear.slice(-2)}` : '';

        localStorage.setItem('accessToken', accessToken);
        localStorage.setItem('user_designation', userInfo?.Designation);
        localStorage.setItem('refreshToken', refreshToken);
        localStorage.setItem('finac_year', yearOnly);
        localStorage.setItem('series_name', seriesName);
        localStorage.setItem('department_id', selectedDepartment);
        const selectedDepartmentName =
          departments.find((dept) => String(dept.ID) === String(selectedDepartment))
            ?.Name || '';
        localStorage.setItem('department_name', selectedDepartmentName);

        if (ipcRenderer) {
          await ipcRenderer.invoke('store-set', 'clientInfo', clientInfo);
          await ipcRenderer.invoke('store-set', 'sysInfo', sysInfo);
          await ipcRenderer.invoke('store-set', 'userInfo', userInfo);
          await ipcRenderer.invoke('store-set', 'generalsettings', generalsettings);
          await ipcRenderer.invoke('store-set', 'user_role', userRole);
          await ipcRenderer.invoke('store-set', 'generalopsettings', generalOpSettings);
          await ipcRenderer.invoke('store-set', 'finac_year', yearOnly);
          await ipcRenderer.invoke('store-set', 'series_name', seriesName);
          await ipcRenderer.invoke('store-set', 'login_mode', loginMode);
          if (loginMode === 'cloud') {
            if (dashBoardPayload)
              await ipcRenderer.invoke('store-set', 'cloud_dashboard_payload', dashBoardPayload);
            if (tenantAuthPayload)
              await ipcRenderer.invoke(
                'store-set',
                'cloud_tenant_auth_payload',
                tenantAuthPayload
              );
            if (phone)
              await ipcRenderer.invoke('store-set', 'cloud_mobile', phone);
            await ipcRenderer.invoke('store-set', 'cloud_otp_verified', true);
          }
          if (rememberMe) {
            await ipcRenderer.invoke('store-set', 'firm_name', firm);
            localStorage.setItem('firm_name', firm);
          }
        } else {
          if (rememberMe) {
            localStorage.setItem('firm_name', firm);
          }
          if (loginMode === 'cloud') {
            try {
              localStorage.setItem('login_mode', 'cloud');
              if (phone) localStorage.setItem('cloud_mobile', phone);
              if (dashBoardPayload)
                localStorage.setItem(
                  'cloud_dashboard_payload',
                  JSON.stringify(dashBoardPayload)
                );
              if (tenantAuthPayload)
                localStorage.setItem(
                  'cloud_tenant_auth_payload',
                  JSON.stringify(tenantAuthPayload)
                );
              localStorage.setItem('cloud_otp_verified', '1');
            } catch (_) {}
          }
          setWebUserInfo({
            clientInfo,
            sysInfo,
            userInfo,
            generalSettings,
            user_role: userRole,
            generalOpSettings,
            yearOnly,
          });
        }

        const is_renewd = false;

        const subscriptionResult = await checkSubscriptionExpiry(accessToken);
        if (!subscriptionResult.allowLogin) {
          return;
        }

        if (subscriptionResult.showWarning) {
          const { daysLeft } = subscriptionResult;
          await Swal.fire({
            icon: 'info',
            title: 'License Expiry Alert',
            text:
              daysLeft === 0
                ? 'Your license expires today.'
                : `Your license expires in ${daysLeft} day${daysLeft > 1 ? 's' : ''}.`,
            confirmButtonText: 'OK',
            allowOutsideClick: false,
          });
        }

        navigate('/dashboard', {
          state: { showAlert: true, clientInfo: clientInfo, is_renewd: is_renewd },
        });
      } else {
        toast.error('Invalid Credentials', {
          autoClose: 1000,
        });
      }
    } catch (error) {
      console.error(error);
      toast.error(error.response?.data?.message || 'Login failed', {
        autoClose: 1000,
      });
    } finally {
      setLoading(false);
    }
  };

  const showLocalForm = loginMode === 'local';
  const showCloudCredentials =
    loginMode === 'cloud' && cloudStep === 'credentials';

  if (!modeHydrated) {
    return (
      <>
        <ToastContainer
          position="top-center"
          autoClose={2000}
          limit={1}
          hideProgressBar={false}
          newestOnTop
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          theme="light"
          transition={Slide}
        />
        <Spinner />
      </>
    );
  }

  return (
    <div className="flex flex-col lg:flex-row min-h-screen bg-gradient-to-br from-gray-100 to-white">
      <ToastContainer
        position="top-center"
        autoClose={2000}
        limit={1}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
        transition={Slide}
      />

      {loading && <Spinner />}

      <div className="lg:w-1/2 flex flex-col items-center justify-center p-6 lg:p-0 relative overflow-hidden">
        <div className="absolute -top-20 -left-20 w-64 h-64 bg-indigo-100 rounded-full opacity-70 blur-3xl"></div>
        <div className="absolute -bottom-20 -right-20 w-80 h-80 bg-purple-100 rounded-full opacity-70 blur-3xl"></div>
        <div className="absolute top-1/3 right-1/4 w-40 h-40 bg-blue-100 rounded-full opacity-50 blur-2xl"></div>

        <div className="w-full max-w-md relative z-10">
          <div className="text-center mb-6">
            <img
              src={`${process.env.PUBLIC_URL}/flogo.png`}
              alt="Logo"
              className="h-40 md:h-48 mx-auto mb-8 transform hover:scale-105 transition-transform duration-300"
            />
            <h1 className="text-4xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-indigo-600 to-purple-600 mb-4">
              Welcome Back
            </h1>
            <p className="text-gray-600 text-lg">Sign in to your account to continue</p>
            {currentVersion && (
              <p className="text-gray-400 text-sm mt-2">Version: {currentVersion}</p>
            )}
          </div>

          <div className="relative bg-white rounded-2xl shadow-xl p-8 pb-14 transition-all duration-300 hover:shadow-2xl border border-gray-100">
            {loginMode === 'cloud' && cloudStep === 'mobile' && (
              <div className="mb-6 space-y-4">
                <p className="text-sm text-gray-600">
                  Enter your registered mobile number. We will send an OTP to verify.
                </p>
                <div className="relative transition-all duration-200 group">
                  <div className="bg-gray-50 rounded-xl pl-12 pr-4 py-4 border border-gray-200 focus-within:border-indigo-500 focus-within:ring-2 focus-within:ring-indigo-300 shadow-sm">
                    <FiSmartphone
                      className="absolute left-4 top-1/2 transform -translate-y-1/2 text-indigo-400"
                      size={20}
                    />
                    <input
                      type="tel"
                      inputMode="numeric"
                      autoComplete="tel"
                      maxLength={10}
                      placeholder="Mobile number"
                      value={phone}
                      onChange={(e) =>
                        setPhone(e.target.value.replace(/\D/g, '').slice(0, 10))
                      }
                      className="w-full bg-transparent focus:outline-none placeholder-gray-400 text-gray-800"
                    />
                  </div>
                </div>
                <button
                  type="button"
                  disabled={loading || phone.length !== 10}
                  onClick={handleCloudMobileContinue}
                  className={`w-full py-4 px-4 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-xl font-medium flex items-center justify-center transition-all duration-300 ${
                    loading || phone.length !== 10
                      ? 'opacity-70 cursor-not-allowed'
                      : 'hover:translate-y-[-2px] hover:shadow-lg'
                  }`}
                >
                  <span className="text-lg">Continue</span>
                  <FiArrowRight className="ml-2" size={20} />
                </button>
              </div>
            )}

            {loginMode === 'cloud' && cloudStep === 'otp' && (
              <div className="mb-6 space-y-4">
                <p className="text-sm text-gray-600">
                  Enter the 4-digit code sent to +91 {phone}
                </p>
                <div className="relative">
                  <div className="bg-gray-50 rounded-xl px-4 py-4 border border-gray-200 focus-within:border-indigo-500 focus-within:ring-2 focus-within:ring-indigo-300">
                    <input
                      type="text"
                      inputMode="numeric"
                      maxLength={4}
                      placeholder="••••"
                      value={otp}
                      onChange={(e) =>
                        setOtp(e.target.value.replace(/\D/g, '').slice(0, 4))
                      }
                      className="w-full bg-transparent focus:outline-none text-center text-2xl tracking-[0.5em] text-gray-800"
                    />
                  </div>
                </div>
                <button
                  type="button"
                  disabled={loading || otp.length !== 4}
                  onClick={handleCloudOtpVerify}
                  className={`w-full py-4 px-4 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-xl font-medium flex items-center justify-center transition-all duration-300 ${
                    loading || otp.length !== 4
                      ? 'opacity-70 cursor-not-allowed'
                      : ''
                  }`}
                >
                  {loading ? (
                    <span>Verifying…</span>
                  ) : (
                    <>
                      <span className="text-lg">Verify & continue</span>
                      <FiArrowRight className="ml-2" size={20} />
                    </>
                  )}
                </button>
                <div className="flex justify-center text-sm text-gray-600 gap-1">
                  <span>Didn&apos;t receive code?</span>
                  <button
                    type="button"
                    disabled={!canResendOtp || loading}
                    onClick={handleResendOtp}
                    className={`font-semibold ${
                      canResendOtp && !loading
                        ? 'text-indigo-600'
                        : 'text-gray-400 cursor-not-allowed'
                    }`}
                  >
                    {canResendOtp ? 'Resend' : `Resend in ${otpTimer}s`}
                  </button>
                </div>
                <button
                  type="button"
                  className="w-full text-sm text-gray-500 hover:text-indigo-600"
                  onClick={() => {
                    setCloudStep('mobile');
                    setOtp('');
                    setVerificationId(null);
                    setAuthToken(null);
                  }}
                >
                  Change mobile number
                </button>
              </div>
            )}

            {showLocalForm && (
              <div className="mb-6">
                <div className="relative transition-all duration-200 group">
                  <div className="bg-gray-50 rounded-xl pl-12 pr-4 py-4 border border-gray-200 focus-within:border-indigo-500 focus-within:ring-2 focus-within:ring-indigo-300 shadow-sm group-hover:shadow-md transition-all duration-300">
                    <HiComputerDesktop
                      className="absolute left-4 top-1/2 transform -translate-y-1/2 text-indigo-400"
                      size={20}
                    />
                    <input
                      type="text"
                      placeholder="Firm Name"
                      value={firm}
                      onChange={(e) => setFirm(e.target.value)}
                      className="w-full bg-transparent focus:outline-none placeholder-gray-400 text-gray-800"
                    />
                  </div>
                </div>

                <div className="mt-3 flex items-center ml-3">
                  <label className="flex items-center cursor-pointer group">
                    <div className="relative">
                      <input
                        type="checkbox"
                        checked={rememberMe}
                        onChange={(e) => setRememberMe(e.target.checked)}
                        className="sr-only"
                      />
                      <div
                        className={`w-4 h-4 rounded border-2 transition-all duration-200 ${
                          rememberMe
                            ? 'bg-indigo-500 border-indigo-500'
                            : 'bg-white border-gray-300 group-hover:border-indigo-400'
                        }`}
                      >
                        {rememberMe && (
                          <svg
                            className="w-3 h-3 text-white absolute top-0.5 left-0.5"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path
                              fillRule="evenodd"
                              d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                              clipRule="evenodd"
                            />
                          </svg>
                        )}
                      </div>
                    </div>
                    <span className="ml-3 text-sm text-gray-600 group-hover:text-gray-800 transition-colors duration-200">
                      Remember firm name
                    </span>
                  </label>
                </div>
              </div>
            )}

            {showCloudCredentials && (
              <div className="mb-6 space-y-3">
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide">
                  Client
                </label>
                <div className="relative transition-all duration-200 group">
                  <div className="bg-gray-50 rounded-xl pl-4 pr-2 py-3 border border-gray-200 focus-within:border-indigo-500 focus-within:ring-2 focus-within:ring-indigo-300 shadow-sm">
                    <select
                      value={selectedTunnelClientId}
                      onChange={handleTunnelClientChange}
                      className="w-full bg-transparent focus:outline-none text-gray-800 appearance-none"
                    >
                      {tunnelClients.map((c) => (
                        <option key={c.clientID} value={String(c.clientID)}>
                          {c.clientName}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            )}

            {(showLocalForm || showCloudCredentials) && (
              <>
                <div className="mb-6">
                  <div className="relative transition-all duration-200 group">
                    <div className="bg-gray-50 rounded-xl pl-12 pr-2  py-2 border border-gray-200 focus-within:border-indigo-500 focus-within:ring-2 focus-within:ring-indigo-300 shadow-sm group-hover:shadow-md transition-all duration-300">
                      <FiUser
                        className="absolute left-4 top-1/2 transform -translate-y-1/2 text-indigo-400"
                        size={20}
                      />
                      <select
                        value={selectedDepartment}
                        onChange={(e) => setSelectedDepartment(e.target.value)}
                        className="w-full bg-transparent focus:outline-none text-gray-800 appearance-none"
                        disabled={departmentsLoading}
                      >
                        <option value="">
                          {departmentsLoading
                            ? 'Loading departments...'
                            : 'Select Department'}
                        </option>
                        {departments.map((department) => (
                          <option key={department.ID} value={String(department.ID)}>
                            {department.Name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>

                <div className="mb-6">
                  <div className="relative transition-all duration-200 group">
                    <div className="bg-gray-50 rounded-xl pl-12 pr-4 py-4 border border-gray-200 focus-within:border-indigo-500 focus-within:ring-2 focus-within:ring-indigo-300 shadow-sm group-hover:shadow-md transition-all duration-300">
                      <FiUser
                        className="absolute left-4 top-1/2 transform -translate-y-1/2 text-indigo-400"
                        size={20}
                      />
                      <input
                        ref={usernameRef}
                        type="text"
                        placeholder="Username"
                        value={username}
                        onChange={(e) => setUsername(e.target.value.toUpperCase())}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            passwordRef.current?.focus();
                          }
                        }}
                        className="w-full bg-transparent focus:outline-none placeholder-gray-400 text-gray-800"
                      />
                    </div>
                  </div>
                </div>

                <div className="mb-8">
                  <div className="relative transition-all duration-200 group">
                    <div className="relative bg-gray-50 rounded-xl pl-12 pr-12 py-4 border border-gray-200 focus-within:border-indigo-500 focus-within:ring-2 focus-within:ring-indigo-300 shadow-sm group-hover:shadow-md transition-all duration-300">
                      <FiLock
                        className="absolute left-4 top-1/2 transform -translate-y-1/2 text-indigo-400 pointer-events-none"
                        size={20}
                      />
                      <input
                        ref={passwordRef}
                        type={showPassword ? 'text' : 'password'}
                        placeholder="Password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            handleLogin();
                          }
                        }}
                        className="w-full bg-transparent focus:outline-none placeholder-gray-400 text-gray-800"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword((v) => !v)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 rounded-md p-1 text-gray-500 hover:text-indigo-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-400"
                        aria-label={showPassword ? 'Hide password' : 'Show password'}
                      >
                        {showPassword ? (
                          <FiEyeOff size={20} aria-hidden />
                        ) : (
                          <FiEye size={20} aria-hidden />
                        )}
                      </button>
                    </div>
                  </div>
                </div>

                <button
                  type="button"
                  disabled={!username || !password || loading}
                  className={`w-full py-4 px-4 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-xl font-medium flex items-center justify-center transition-all duration-300 transform hover:translate-y-[-2px] hover:shadow-lg ${
                    !username || !password || loading
                      ? 'opacity-70 cursor-not-allowed'
                      : ''
                  }`}
                  onClick={handleLogin}
                >
                  <span className="text-lg">Sign In</span>
                  <FiArrowRight className="ml-2" size={20} />
                </button>
                {showCloudCredentials && (
                  <div className="mt-3 flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-sm">
                    <button
                      type="button"
                      disabled={loading}
                      onClick={handleRefetchCloudFirms}
                      className="bg-transparent p-0 font-medium text-indigo-600 hover:text-indigo-800 hover:underline disabled:cursor-not-allowed disabled:opacity-50 disabled:no-underline"
                    >
                      Refetch firms
                    </button>
                    <span className="text-gray-300 select-none" aria-hidden>
                      |
                    </span>
                    <button
                      type="button"
                      disabled={loading}
                      onClick={handleChangeCloudNumber}
                      className="bg-transparent p-0 font-medium text-gray-600 hover:text-gray-900 hover:underline disabled:cursor-not-allowed disabled:opacity-50 disabled:no-underline"
                    >
                      Change mobile number
                    </button>
                  </div>
                )}
              </>
            )}

            {CAN_USE_LOCAL_MODE ? (
              <div className="pointer-events-auto absolute bottom-3 left-3 flex items-center gap-1.5">
                <span
                  className={`text-[10px] font-semibold uppercase tracking-wide ${
                    loginMode === 'local' ? 'text-indigo-600' : 'text-gray-400'
                  }`}
                >
                  Local
                </span>
                <button
                  type="button"
                  aria-label={
                    loginMode === 'cloud' ? 'Switch to local login' : 'Switch to cloud login'
                  }
                  onClick={() =>
                    handleLoginModeChange(loginMode === 'local' ? 'cloud' : 'local')
                  }
                  className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full border border-gray-200 transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:ring-offset-1 ${
                    loginMode === 'cloud' ? 'bg-indigo-500' : 'bg-gray-200'
                  }`}
                >
                  <span
                    className={`absolute top-0.5 left-0.5 h-4 w-4 rounded-full bg-white shadow transition-transform duration-200 ease-out ${
                      loginMode === 'cloud' ? 'translate-x-4' : 'translate-x-0'
                    }`}
                  />
                </button>
                <span
                  className={`text-[10px] font-semibold uppercase tracking-wide ${
                    loginMode === 'cloud' ? 'text-indigo-600' : 'text-gray-400'
                  }`}
                >
                  Cloud
                </span>
              </div>
            ) : (
              /* Browser build: cloud is the only reachable mode, so show it as a fixed badge. */
              <div className="pointer-events-none absolute bottom-3 left-3 flex items-center">
                <span
                  title="Local login is available in the desktop app only"
                  className="inline-flex items-center rounded-full border border-indigo-100 bg-indigo-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-indigo-600"
                >
                  Cloud
                </span>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="lg:w-1/2 hidden lg:block relative bg-gradient-to-br from-indigo-600 to-purple-700 overflow-hidden">
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="w-[150%] h-[150%] absolute">
            <div className="absolute top-[-10%] left-[-10%] w-full h-full bg-indigo-600 rounded-full opacity-30 blur-3xl animate-pulse-slow"></div>
            <div className="absolute bottom-[-20%] right-[-10%] w-full h-full bg-purple-700 rounded-full opacity-30 blur-3xl animate-pulse-slow animation-delay-1000"></div>
          </div>
          <div className="w-full h-full absolute bg-gradient-to-b from-transparent via-transparent to-indigo-900/70"></div>
        </div>

        <div className="absolute inset-0">
          <img
            src={`${process.env.PUBLIC_URL}/loginimg2.png`}
            alt="Background"
            className="w-full h-full object-cover opacity-40 mix-blend-overlay"
          />
        </div>

        <div className="relative h-full flex items-center justify-center p-10">
          <div className="bg-white/10 backdrop-blur-md rounded-3xl border border-white/20 p-4 w-3/4 max-w-xl max-h-[96] shadow-2xl transform transition-all duration-500 hover:scale-105">
            <div className="mb-2">
              <h2 className="text-3xl font-bold text-white leading-tight drop-shadow-md">
                Redefining Pharmacy
                <br /> Excellence
              </h2>
              <p className="text-white/90 mt-2 text-sm">
                Advanced solutions for modern healthcare pharmacy
              </p>
            </div>

            <div className="mt-3 rounded-xl overflow-hidden shadow-2xl transform transition-all duration-500 hover:scale-[1.02] hover:shadow-indigo-500/30">
              <img
                src={`${process.env.PUBLIC_URL}/loginimg1.png`}
                alt="Lab Excellence"
                className="w-full h-full object-cover shadow-lg"
              />
            </div>

            <div className="flex gap-4 mt-3">
              <div className="h-10 w-10 rounded-full bg-white/20 backdrop-blur-md flex items-center justify-center transition-transform hover:scale-110 cursor-pointer">
                <FiActivity className="text-white" size={20} />
              </div>
              <div className="h-10 w-10 rounded-full bg-gradient-to-br from-indigo-400 to-purple-400 flex items-center justify-center transition-transform hover:scale-110 cursor-pointer text-white font-bold text-lg">
                <span>1.0</span>
              </div>
            </div>
          </div>
        </div>

        <div className="absolute top-[20%] left-[10%] w-20 h-20 bg-white/10 backdrop-blur-sm rounded-xl rotate-12 animate-float"></div>
        <div className="absolute bottom-[15%] right-[15%] w-16 h-16 bg-purple-400/20 backdrop-blur-sm rounded-xl -rotate-12 animate-float animation-delay-1000"></div>
      </div>

      <style>
        {`
      @keyframes fade-in-up {
        from {
          opacity: 0;
          transform: translateY(20px);
        }
        to {
          opacity: 1;
          transform: translateY(0);
        }
      }
      .animate-fade-in-up {
        animation: fade-in-up 0.3s ease-out forwards;
      }
      `}
      </style>
    </div>
  );
};

export default LoginPage;
